import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ADMIN_QUERY_KEYS, pendingMenuItemsQueryOptions } from '@/api/queries/admin';
import { MENU_ITEMS_QUERY_KEYS } from '@/api/queries/menu-items';
import * as adminApi from '@/api/endpoints/admin';
import { ListPendingMenuItemsParams } from '@/api/endpoints/admin';

/**
 * Query hook for listing pending menu items
 */
export function useAdminPendingMenuItems(params: ListPendingMenuItemsParams = {}) {
  return useQuery(pendingMenuItemsQueryOptions(params));
}

/**
 * Mutation hook for approving a menu item
 */
export function useAdminApproveMenuItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return adminApi.approveMenuItem(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.pendingMenuItems() });
      queryClient.invalidateQueries({ queryKey: MENU_ITEMS_QUERY_KEYS.all });
    },
  });
}

/**
 * Mutation hook for updating a menu item
 */
export function useAdminUpdateMenuItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: { name?: string; description?: string };
    }) => {
      return adminApi.updateMenuItem(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.pendingMenuItems() });
      queryClient.invalidateQueries({ queryKey: MENU_ITEMS_QUERY_KEYS.all });
    },
  });
}

/**
 * Mutation hook for deleting a menu item
 */
export function useAdminDeleteMenuItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return adminApi.deleteMenuItem(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.pendingMenuItems() });
      queryClient.invalidateQueries({ queryKey: MENU_ITEMS_QUERY_KEYS.all });
    },
  });
}
